import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Leaf,
  User,
  Mail,
  Phone,
  DollarSign,
  CreditCard,
  FileText,
  CheckCircle,
  Loader2,
  Building
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOAN_TYPES = [
  { value: 'DSCR', label: 'DSCR Loan' },
  { value: 'HELOC', label: 'Home Equity Line of Credit (HELOC)' },
  { value: 'Personal', label: 'Personal Loan' },
  { value: 'Construction', label: 'Construction Loan' },
  { value: 'Commercial', label: 'Commercial Loan' },
  { value: 'Other', label: 'Other' }
];

const CREDIT_SCORE_RANGES = [
  { value: 'Excellent (750+)', label: 'Excellent (750+)' },
  { value: 'Good (700-749)', label: 'Good (700-749)' },
  { value: 'Fair (650-699)', label: 'Fair (650-699)' },
  { value: 'Poor (below 650)', label: 'Poor (below 650)' },
  { value: 'Not sure', label: 'Not sure' }
];

const PublicLeadCapturePage = () => {
  const { referralCode } = useParams();
  const [searchParams] = useSearchParams();
  
  // Get referral code from URL param or query string
  const code = referralCode || searchParams.get('ref');
  
  const [broker, setBroker] = useState(null);
  const [loading, setLoading] = useState(!!code);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    loan_type: '',
    loan_amount: '',
    credit_score: '',
    referral_code: code || ''
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (code) {
      loadBrokerInfo(code);
    }
  }, [code]);

  const loadBrokerInfo = async (brokerCode) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/broker/${brokerCode}`);
      setBroker(res.data);
      setFormData(prev => ({ ...prev, referral_code: brokerCode }));
    } catch (error) {
      console.log('Broker not found, continuing without referral');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-\(\)\+]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount) : null
      };
      
      const res = await axios.post(`${BACKEND_URL}/api/public/lead`, submitData);
      
      if (res.data.success) {
        setSubmitted(true);
        toast.success('Application submitted successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <Toaster position="top-center" richColors />
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Application Submitted!</h2>
            <p className="text-slate-400 mb-6">
              Thank you for your interest. A loan specialist will contact you within 24 hours to discuss your options.
            </p>
            {broker && (
              <p className="text-sm text-slate-500">
                Referred by: {broker.name}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="public-lead-capture-page">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-600 rounded-lg">
              <Leaf className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-white">Great Leaf Lending</h1>
              <p className="text-xs text-slate-400">Your Trusted Loan Partner</p>
            </div>
          </div>
          {broker && (
            <div className="text-right hidden sm:block">
              <p className="text-sm text-slate-400">Referred by</p>
              <p className="font-medium text-emerald-400">{broker.name}</p>
              {broker.company && (
                <p className="text-xs text-slate-500">{broker.company}</p>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 md:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Get Started on Your Loan Today
            </h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              Fill out the form below and one of our loan specialists will reach out to discuss your financing options.
            </p>
          </div>

          {/* Form Card */}
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-500" />
                Loan Application
              </CardTitle>
              <CardDescription className="text-slate-400">
                Fields marked with * are required
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Personal Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-slate-300">First Name *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => handleChange('first_name', e.target.value)}
                        placeholder="John"
                        className={`bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500 ${errors.first_name ? 'border-red-500' : ''}`}
                        data-testid="lead-first-name"
                      />
                      {errors.first_name && (
                        <p className="text-xs text-red-400">{errors.first_name}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="text-slate-300">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => handleChange('last_name', e.target.value)}
                        placeholder="Smith"
                        className={`bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500 ${errors.last_name ? 'border-red-500' : ''}`}
                        data-testid="lead-last-name"
                      />
                      {errors.last_name && (
                        <p className="text-xs text-red-400">{errors.last_name}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Contact Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-300">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="john.smith@email.com"
                        className={`bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500 ${errors.email ? 'border-red-500' : ''}`}
                        data-testid="lead-email"
                      />
                      {errors.email && (
                        <p className="text-xs text-red-400">{errors.email}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-300">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        className={`bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500 ${errors.phone ? 'border-red-500' : ''}`}
                        data-testid="lead-phone"
                      />
                      {errors.phone && (
                        <p className="text-xs text-red-400">{errors.phone}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Loan Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Loan Information (Optional)
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="loan_type" className="text-slate-300">Type of Loan</Label>
                      <Select
                        value={formData.loan_type}
                        onValueChange={(value) => handleChange('loan_type', value)}
                      >
                        <SelectTrigger 
                          className="bg-slate-900/50 border-slate-600 text-white focus:border-emerald-500"
                          data-testid="lead-loan-type"
                        >
                          <SelectValue placeholder="Select loan type" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {LOAN_TYPES.map(type => (
                            <SelectItem 
                              key={type.value} 
                              value={type.value}
                              className="text-white hover:bg-slate-700"
                            >
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="loan_amount" className="text-slate-300">Desired Loan Amount</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                          <Input
                            id="loan_amount"
                            type="number"
                            value={formData.loan_amount}
                            onChange={(e) => handleChange('loan_amount', e.target.value)}
                            placeholder="100,000"
                            className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500 pl-10"
                            data-testid="lead-loan-amount"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="credit_score" className="text-slate-300">Credit Score Range</Label>
                        <Select
                          value={formData.credit_score}
                          onValueChange={(value) => handleChange('credit_score', value)}
                        >
                          <SelectTrigger 
                            className="bg-slate-900/50 border-slate-600 text-white focus:border-emerald-500"
                            data-testid="lead-credit-score"
                          >
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            {CREDIT_SCORE_RANGES.map(range => (
                              <SelectItem 
                                key={range.value} 
                                value={range.value}
                                className="text-white hover:bg-slate-700"
                              >
                                {range.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
                    disabled={submitting}
                    data-testid="lead-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-4">
                    By submitting this form, you agree to be contacted regarding your loan inquiry.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Trust Indicators */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-slate-500 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              <span>Fast Approval</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              <span>Competitive Rates</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              <span>Expert Support</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-6 mt-12">
        <div className="container mx-auto px-6 text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Great Leaf Lending. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicLeadCapturePage;
